import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Trans } from 'react-i18next';
import { startOfMinute } from 'date-fns';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import { Typography } from '@components/Typography';
import { InternalLink } from '@components/Link';
import { ViewToggle, ViewToggleButton } from '@components/ViewToggle';
import { useAccountPeople, usePermissions, useTranslation, userCoversTeam } from '@hooks';
import { ConversationStatus } from '@constants/conversations';
import { InboxHistory, MessageStatus } from '@constants/inbox';
import { CHANNEL, STATUS } from '@constants/channels';
import { RoutePath } from '@constants/routes';
import { InboxEmptyIcon } from 'assets/images/icons';
import Message from './Message';
import ConversationsEvent from './ConversationsEvent';
import InboxInput from './InboxInput';
import NoteInput from './NoteInput';
import InboxHeader from './InboxHeader';
import SendWhatsAppTemplate from './SendWhatsAppTemplate';
import { Container, Header, Feed, Footer } from './styles';

const COMPOSER_MODE = {
    Reply: 'reply',
    Note: 'note',
};

const Inbox = ({ isMobile, filterRef, activeConversation, setActiveConversation, setContactOpened }) => {
    const [whatsAppTemplateModalOpen, setWhatsAppTemplateModalOpen] = useState(false);
    const [composerMode, setComposerMode] = useState(COMPOSER_MODE.Reply);

    useEffect(() => {
        setComposerMode(COMPOSER_MODE.Reply);
    }, [activeConversation?.id]);

    const { data: messages, loading } = useSelector(state => state.inbox);
    const account = useSelector(state => state.account?.account);
    const currentUser = useSelector(state => state.auth.user);
    const { isLoaded, getPerson, getPersonName } = useAccountPeople();
    const agents = useSelector(state => state.agentsLookup?.data || []);
    const locations = useSelector(state => state.account?.account?.companies || []);
    const channels = useSelector(state => state.channels?.data || []);

    const { t } = useTranslation();
    const { hasAccess } = usePermissions();

    const isConversationOpen = activeConversation?.status === ConversationStatus.Open;

    const messagesHistory = useMemo(() => {
        const sortedMessages = [...messages].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

        const getNormalizedDate = dateStr => startOfMinute(new Date(dateStr)).getTime();

        const createMessageObject = msg => ({
            id: msg.id,
            content: msg.content,
            contentType: msg.contentType,
            createdAt: msg.createdAt,
            replyMessage: msg.replyMessage,
            senderType: msg.senderType,
            status: msg.status,
            metadata: msg.metadata,
        });

        return sortedMessages.reduce((groups, message) => {
            const lastGroup = groups[groups.length - 1];
            const messageTime = getNormalizedDate(message.createdAt);

            const shouldCreateNewGroup =
                !lastGroup ||
                messageTime !== getNormalizedDate(lastGroup.createdAt) ||
                message.type !== lastGroup.type ||
                message.modelType !== lastGroup.modelType ||
                message.senderId !== lastGroup.senderId ||
                message.senderType !== lastGroup.senderType ||
                message.senderAgentId !== lastGroup.senderAgentId ||
                message.status === MessageStatus.Failed;

            if (shouldCreateNewGroup) {
                groups.push({
                    ...message,
                    children: [createMessageObject(message)],
                    createdAt: new Date(messageTime),
                });
            } else {
                lastGroup.children.push(createMessageObject(message));
                lastGroup.children.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
            }

            return groups;
        }, []);
    }, [messages]);

    const isArchived = activeConversation?.channelId === CHANNEL.ARCHIVED;
    const conversationChannel = channels.find(channel => channel.id === activeConversation?.channelId);
    const isVisitorUnreachable =
        conversationChannel?.type === CHANNEL.LIVE_CHAT && !!conversationChannel.followUpChannelMissing;
    const isMessagingWindowClosed = !!activeConversation?.messagingWindowClosed;
    const isWhatsAppInactive = isMessagingWindowClosed && activeConversation?.channelType === CHANNEL.WHATSAPP_BUSINESS;
    const assignedUser = getPerson(activeConversation?.assignedUserId);
    const isAssignedToOtherUser =
        !!activeConversation?.assignedUserId &&
        activeConversation.assignedUserId !== currentUser?.id &&
        (!isLoaded || (!!assignedUser && !assignedUser.isRemoved));
    // Mirrors the backend single-responder guard so actions the API would reject are not offered
    const canRespond = !activeConversation?.assignedAgentId && !isAssignedToOtherUser;
    // Mirror the backend team guard: cross-team collaborators read/note only; reply, templates and resend would 403
    const canReplyByTeam = userCoversTeam(currentUser, activeConversation?.teamId);

    return (
        <Container hasConversation={!!activeConversation}>
            {activeConversation && (
                <>
                    <Header>
                        <InboxHeader
                            isMobile={isMobile}
                            setContactOpened={setContactOpened}
                            activeConversation={activeConversation}
                            setActiveConversation={setActiveConversation}
                            filterRef={filterRef}
                            assignedUser={assignedUser}
                            isAssignedToOtherUser={isAssignedToOtherUser}
                            canReplyByTeam={canReplyByTeam}
                        />
                    </Header>
                    <Feed>
                        {!loading &&
                            messagesHistory.map((item, index) =>
                                item.modelType === InboxHistory.Message ? (
                                    <Message
                                        key={item.id}
                                        data={item}
                                        agents={agents}
                                        locations={locations}
                                        assignedLocation={
                                            activeConversation?.locations?.length === 1
                                                ? activeConversation?.locations[0]
                                                : null
                                        }
                                        channel={activeConversation?.channelType}
                                        channelStatus={activeConversation?.channelStatus}
                                        canResend={canRespond && canReplyByTeam && !isMessagingWindowClosed}
                                    />
                                ) : (
                                    <ConversationsEvent key={`event-${index}`} data={item} agents={agents} />
                                ),
                            )}
                    </Feed>
                    <Footer>
                        <ViewToggle sx={{ marginBottom: '8px' }}>
                            <ViewToggleButton
                                type="button"
                                active={composerMode === COMPOSER_MODE.Reply}
                                onClick={() => setComposerMode(COMPOSER_MODE.Reply)}
                            >
                                {t('InboxPage.replyTab')}
                            </ViewToggleButton>
                            <ViewToggleButton
                                type="button"
                                active={composerMode === COMPOSER_MODE.Note}
                                onClick={() => setComposerMode(COMPOSER_MODE.Note)}
                            >
                                <VisibilityOffOutlinedIcon sx={{ fontSize: 14 }} />
                                {t('InboxPage.noteTab')}
                            </ViewToggleButton>
                        </ViewToggle>
                        {composerMode === COMPOSER_MODE.Note ? (
                            // Keyed by conversation so the uncontrolled TipTap editor resets and no draft carries over
                            <NoteInput key={activeConversation?.id} activeConversation={activeConversation} />
                        ) : isArchived ? (
                            <Typography
                                variant="caption"
                                sx={{ textAlign: 'center', display: 'block', color: 'var(--text-secondary)' }}
                            >
                                {t('InboxPage.archivedConversation')}
                            </Typography>
                        ) : activeConversation?.channelStatus === STATUS.DISCONNECTED ? (
                            <Typography
                                variant="caption"
                                sx={{ textAlign: 'center', display: 'block', color: 'var(--text-secondary)' }}
                            >
                                <Trans t={t} i18nKey="InboxPage.disconnectedChannel">
                                    <InternalLink
                                        to={RoutePath.AccountSettingsChannels}
                                        variant="caption"
                                        align="center"
                                        underline="hover"
                                    >
                                        {{ link: t('InboxPage.settings') }}
                                    </InternalLink>
                                </Trans>
                            </Typography>
                        ) : activeConversation?.isImported ? (
                            <Typography
                                variant="caption"
                                sx={{ textAlign: 'center', display: 'block', color: 'var(--text-secondary)' }}
                            >
                                {t('InboxPage.importedConversation')}
                            </Typography>
                        ) : !isConversationOpen ? (
                            <Typography
                                variant="caption"
                                sx={{ textAlign: 'center', display: 'block', color: 'var(--text-secondary)' }}
                            >
                                {t('InboxPage.closedConversation')}
                            </Typography>
                        ) : !canReplyByTeam ? (
                            <Typography
                                variant="caption"
                                sx={{ textAlign: 'center', display: 'block', color: 'var(--text-secondary)' }}
                            >
                                {t('InboxPage.collaboratorReadOnly')}
                            </Typography>
                        ) : activeConversation?.assignedAgentId ? (
                            <Typography
                                variant="caption"
                                sx={{ textAlign: 'center', display: 'block', color: 'var(--text-secondary)' }}
                            >
                                {t('InboxPage.conversationAssignedOnPulse')}
                            </Typography>
                        ) : isAssignedToOtherUser ? (
                            <Typography
                                variant="caption"
                                sx={{ textAlign: 'center', display: 'block', color: 'var(--text-secondary)' }}
                            >
                                {t('InboxPage.conversationAssignedToUser', {
                                    name: getPersonName(activeConversation?.assignedUserId, '-'),
                                })}
                            </Typography>
                        ) : isWhatsAppInactive ? (
                            <Typography
                                variant="caption"
                                sx={{ textAlign: 'center', display: 'block', color: 'var(--text-secondary)' }}
                            >
                                <Trans t={t} i18nKey="InboxPage.whatsApp24HoursWindow">
                                    <Typography
                                        onClick={() => setWhatsAppTemplateModalOpen(true)}
                                        variant="caption"
                                        sx={{
                                            color: 'var(--primary-main)',
                                            cursor: 'pointer',
                                            '&:hover': { textDecoration: 'underline' },
                                        }}
                                    >
                                        {{ link: t('InboxPage.useApprovedMessage') }}
                                    </Typography>
                                </Trans>
                            </Typography>
                        ) : isMessagingWindowClosed ? (
                            <Typography
                                variant="caption"
                                sx={{ textAlign: 'center', display: 'block', color: 'var(--text-secondary)' }}
                            >
                                {t('InboxPage.messagingWindowClosed')}
                            </Typography>
                        ) : (
                            <>
                                {isVisitorUnreachable ? (
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            textAlign: 'center',
                                            display: 'block',
                                            color: 'var(--text-secondary)',
                                            marginBottom: '8px',
                                        }}
                                    >
                                        <Trans t={t} i18nKey="InboxPage.liveChatVisitorUnreachable">
                                            <InternalLink
                                                to={RoutePath.AccountSettingsChannels}
                                                variant="caption"
                                                align="center"
                                                underline="hover"
                                            >
                                                {{ link: t('InboxPage.settings') }}
                                            </InternalLink>
                                        </Trans>
                                    </Typography>
                                ) : null}
                                <InboxInput
                                    activeConversation={activeConversation}
                                    surveysEnabled={account?.surveysEnabled && hasAccess('surveys')}
                                    couponsEnabled={account?.couponsEnabled && hasAccess('coupons')}
                                />
                            </>
                        )}
                    </Footer>
                </>
            )}
            {!activeConversation && (
                <div
                    style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}
                >
                    <InboxEmptyIcon />
                    <Typography variant="body1" sx={{ textAlign: 'center', marginTop: '16px' }}>
                        {t('InboxPage.noConversationsSelected')}
                    </Typography>
                </div>
            )}
            {whatsAppTemplateModalOpen ? (
                <SendWhatsAppTemplate
                    isOpen={whatsAppTemplateModalOpen}
                    onClose={() => setWhatsAppTemplateModalOpen(false)}
                    channelId={activeConversation?.channelId}
                    conversationId={activeConversation?.id}
                />
            ) : null}
        </Container>
    );
};

export default Inbox;
